import React from 'react';
import * as Styles from './phoneticsmodal.styles';
import { Button, Checkbox, Container, Icon, Item } from 'semantic-ui-react';
import { LanguageSelector } from 'components/LanguageSelector';
import { useUpsertLexeme } from 'api/phoneticsApi';
import { LanguageCode } from '@visikon/core-models/languageTypes';
import {
  accepted_consonants,
  accepted_support_characters,
  accepted_vowels,
  hasUnacceptedValues,
  sayAsHasUnacceptedValues,
} from './phoneticsValidation';
import { copyToClipboard } from 'commons/utils';
import { logger } from '@sentry/react';
import { PhoneticsAudioButton } from './PhoneticsAudioButton';
import { SelectLexemeFn } from './PhoneticsList';
import { LexemeCreationEntry, LexemeTypesEnum } from '@visikon/core-models';

export function PhoneticEditor({
  lexeme,
  language,
  onLanguageChanged,
  onSelectLexeme,
  selectedVoiceId,
  manuscript,
}: {
  lexeme?: LexemeCreationEntry;
  language: LanguageCode;
  onLanguageChanged: (language: LanguageCode) => void;
  onSelectLexeme: SelectLexemeFn;
  selectedVoiceId: string;
  manuscript: string;
}) {
  const [graphemeText, setGraphemeText] = React.useState('');
  const [phoneme, setPhonemeText] = React.useState('');
  const [sayAsText, setSayAsText] = React.useState('');
  const mutator = useUpsertLexeme(onSelectLexeme);
  const [isMissingRequiredField, setIsMissingRequiredField] = React.useState(false);
  const isCreatingNewEntry = !lexeme?.id;
  const [manuscriptText, setManuscriptText] = React.useState(manuscript);
  const [sayAsToggle, setSayAsToggle] = React.useState(false);

  const phonemeInputRef = React.useRef<HTMLTextAreaElement>(null);
  const sayAsInputRef = React.useRef<HTMLTextAreaElement>(null);
  const graphemeInputRef = React.useRef<HTMLInputElement>(null);
  const stemEditorRef = React.useRef<HTMLInputElement>(null);
  const descriptionEditorRef = React.useRef<HTMLInputElement>(null);

  const checkForMissingRequiredFields = React.useCallback(() => {
    // Only the textarea of the active mode (phonetic or say-as) is mounted
    // Trim so whitespace-only say-as text (spaces are valid there) counts as missing
    const contentToCheck = (phonemeInputRef.current ?? sayAsInputRef.current)?.value.trim() || '';
    const graphemeToCheck = graphemeInputRef.current?.value || '';
    const stem = stemEditorRef.current?.value || '';
    const description = descriptionEditorRef.current?.value || '';
    const missingField = !contentToCheck || !graphemeToCheck || !stem || !description;
    setIsMissingRequiredField(missingField);
  }, []);

  // When lexeme changes, update the editor fields with the new lexeme's data
  React.useEffect(() => {
    // Entries without a type predate say-as support and are phonetic
    const lexemePhoneme = lexeme && lexeme.type !== LexemeTypesEnum.SAY_AS ? lexeme.phoneme || '' : '';
    const lexemeSayAs = lexeme?.type === LexemeTypesEnum.SAY_AS ? lexeme.sayAs || '' : '';
    setSayAsToggle(lexeme?.type === LexemeTypesEnum.SAY_AS);
    setPhonemeText(lexemePhoneme);
    setSayAsText(lexemeSayAs);
    setGraphemeText(lexeme?.grapheme || '');

    if (graphemeInputRef.current) graphemeInputRef.current.value = lexeme?.grapheme || '';
    if (phonemeInputRef.current) phonemeInputRef.current.value = lexemePhoneme;
    if (sayAsInputRef.current) sayAsInputRef.current.value = lexemeSayAs;
    if (stemEditorRef.current) stemEditorRef.current.value = lexeme?.stem || '';
    if (descriptionEditorRef.current) descriptionEditorRef.current.value = lexeme?.description || '';
    checkForMissingRequiredFields();
  }, [checkForMissingRequiredFields, lexeme]);

  // Re-check required fields once the textarea of the newly selected mode is mounted
  React.useEffect(() => {
    checkForMissingRequiredFields();
  }, [checkForMissingRequiredFields, sayAsToggle]);

  const handleGraphemeChange = (value: string) => {
    setGraphemeText(value);
    checkForMissingRequiredFields();
  };

  const handlePhonemeChange = (textValue: string) => {
    setPhonemeText(textValue);
    checkForMissingRequiredFields();
  };

  const handleSayAsChange = (textValue: string) => {
    setSayAsText(textValue);
    checkForMissingRequiredFields();
  };

  const handlePhonemeSanitized = React.useCallback(
    (sanitizedPhoneme: string) => {
      setPhonemeText(sanitizedPhoneme);

      if (phonemeInputRef.current) {
        phonemeInputRef.current.value = sanitizedPhoneme;
      }

      checkForMissingRequiredFields();
    },
    [checkForMissingRequiredFields],
  );

  const containsUnacceptedValues = !sayAsToggle ? hasUnacceptedValues(phoneme) : sayAsHasUnacceptedValues(sayAsText);

  const handlePhonemeCopy = async () => {
    const target = phonemeInputRef.current!;
    const selectedText = target.value.substring(target.selectionStart, target.selectionEnd);

    if (!selectedText && !phoneme) {
      return;
    }

    try {
      await copyToClipboard(selectedText || phoneme);
    } catch (error) {
      logger.error('Failed to copy phoneme to clipboard:', error);
    }
  };

  const handlePassToEditor = React.useCallback(
    (passedCharacter: string) => {
      const editor = phonemeInputRef.current!;
      const selectionStart = editor.selectionStart || 0;
      const selectionEnd = editor.selectionEnd || selectionStart;

      // Replace the selected range, or insert at the caret when there is no selection.
      const newPhoneme = [editor.value.slice(0, selectionStart), passedCharacter, editor.value.slice(selectionEnd)].join('');

      // Update the state and value of input field
      setPhonemeText(newPhoneme);
      editor.value = newPhoneme;

      // Set the cursor position after the inserted character
      const newPosition = selectionStart + passedCharacter.length;
      editor.setSelectionRange(newPosition, newPosition);
      checkForMissingRequiredFields();
    },
    [checkForMissingRequiredFields],
  );

  const clickable_vowelList = accepted_vowels(handlePassToEditor);
  const clickable_consonantList = accepted_consonants(handlePassToEditor);
  const clickable_supportCharList = accepted_support_characters(handlePassToEditor);

  const isUpdateDisabled = isMissingRequiredField || containsUnacceptedValues;

  const handleUpdateEntry = () => {
    const contentInputRef = sayAsToggle ? sayAsInputRef : phonemeInputRef;
    const valid: boolean = [contentInputRef, graphemeInputRef, stemEditorRef, descriptionEditorRef].every(
      (inputRef) => inputRef.current?.reportValidity() ?? false,
    );

    if (isUpdateDisabled || !valid) {
      return;
    }

    const entry = {
      id: lexeme?.id,
      grapheme: graphemeInputRef.current!.value,
      stem: stemEditorRef.current!.value,
      description: descriptionEditorRef.current!.value,
    };

    mutator.mutate({
      lexeme: sayAsToggle
        ? { ...entry, type: LexemeTypesEnum.SAY_AS, sayAs: sayAsInputRef.current!.value.trim() }
        : { ...entry, type: LexemeTypesEnum.PHONETIC, phoneme: phonemeInputRef.current!.value },
      language,
    });
  };

  const freeTextAreaValidation =
    manuscriptText.length === 0 || (graphemeText !== '' ? !manuscriptText.toLowerCase().includes(graphemeText.toLowerCase()) : true);

  return (
    <Container>
      <Styles.TestModalTitle>Grapheme</Styles.TestModalTitle>
      <Styles.TestableTextEditor
        ref={graphemeInputRef}
        onChange={({ target }) => handleGraphemeChange(target.value)}
        value={graphemeText}
        spellCheck="false"
        minLength={1}
        disabled={!isCreatingNewEntry}
        pattern="^[\p{Letter}\p{Number}]+(?:-+[\p{Letter}\p{Number}]+)*$"
        onInvalid={inputValidationMessage('Use only letters and numbers. No special characters allowed, except for hyphens between words.')}
      />
      <Styles.TestModalTitle>Word stem</Styles.TestModalTitle>
      <Styles.StemEditor
        ref={stemEditorRef}
        onChange={checkForMissingRequiredFields}
        placeholder="e.g. smile (for the word smiles)"
        lang={language}
        spellCheck="false"
        minLength={1}
        pattern="^[\p{Letter}\p{Number}]+$"
        onInvalid={inputValidationMessage('Use only letters and numbers. No spaces or special characters allowed.')}
      />
      <Styles.TestModalTitle>Description</Styles.TestModalTitle>
      <Styles.DescriptionEditor
        ref={descriptionEditorRef}
        onChange={checkForMissingRequiredFields}
        placeholder="Short description, e.g. 'a tool to dig holes'"
        lang={language}
        minLength={2}
      />
      <div style={{ display: 'flex', paddingTop: '1.5rem' }}>
        {!sayAsToggle && (
          <Styles.TestModalTitle>
            Phonetics text{' '}
            <span style={{ fontSize: '0.8rem', display: 'inline-block' }}>
              <a
                href="https://learn.microsoft.com/en-us/azure/ai-services/speech-service/speech-ssml-phonetic-sets#da-dk"
                target="_blank"
                rel="noreferrer"
              >
                &lt;&lt; IPA alphabet Danish &gt;&gt;
              </a>
            </span>
          </Styles.TestModalTitle>
        )}
        {sayAsToggle && <Styles.TestModalTitle>Say as</Styles.TestModalTitle>}
        <Styles.SayAsToggle>
          <Checkbox toggle label="Phonetic/Say-As" checked={sayAsToggle} onChange={(_, { checked }) => setSayAsToggle(!!checked)} />
        </Styles.SayAsToggle>
      </div>
      {!sayAsToggle && (
        <div style={{ position: 'relative', width: '100%' }}>
          <Styles.PhoneticsEditor
            ref={phonemeInputRef}
            onChange={({ target }) => handlePhonemeChange(target.value)}
            value={phoneme}
            spellCheck="false"
          />
          <Styles.CopyIcon name="copy" color="grey" onClick={handlePhonemeCopy} />
        </div>
      )}
      {sayAsToggle && (
        <div>
          <Styles.PhoneticsEditor
            ref={sayAsInputRef}
            onChange={({ target }) => handleSayAsChange(target.value)}
            value={sayAsText}
            spellCheck="false"
          />
        </div>
      )}
      {!sayAsToggle && (
        <Styles.PhoneticInfo>
          <p>Phonetic vowels: {clickable_vowelList}</p>
          <p>Phonetic consonants: {clickable_consonantList}</p>
          <p>Phonetic support characters: {clickable_supportCharList}</p>
        </Styles.PhoneticInfo>
      )}
      <PhoneticsAudioButton
        text={graphemeText}
        graphemeText={graphemeText}
        phoneme={phoneme}
        sayAs={sayAsToggle ? sayAsText : undefined}
        language={language}
        selectedVoiceId={selectedVoiceId}
        disabled={containsUnacceptedValues}
        onPhonemeSanitized={handlePhonemeSanitized}
      />
      <Button
        name="UpdateEntry"
        color="green"
        floated="right"
        disabled={isUpdateDisabled}
        loading={mutator.isLoading}
        onClick={() => {
          handleUpdateEntry();
        }}
      >
        {isCreatingNewEntry ? 'Add Entry' : 'Update Entry'}
      </Button>

      {isMissingRequiredField && (
        <Item>
          <Icon name="warning circle" color="orange" />
          Grapheme, {sayAsToggle ? 'say-as text' : 'phonetics'}, stem and description are all required
        </Item>
      )}

      {mutator.isError && (
        <Item>
          <Icon name="warning circle" color="red" />
          Failed to save phonetics entry: {mutator.error.response?.data?.message || mutator.error?.message}
        </Item>
      )}

      {containsUnacceptedValues && (
        <Item>
          <Icon name="warning circle" color="red" />
          {sayAsToggle ? "Say-as text may only contain letters, numbers, spaces and the characters . , ' -" : 'Non-phonetic sign detected'}
        </Item>
      )}

      <Styles.TestModalTitle>Free text</Styles.TestModalTitle>
      <Styles.FreeTextPhoneticEditor value={manuscriptText} onChange={(e) => setManuscriptText(e.target.value)} lang={language} spellCheck="false" />
      <PhoneticsAudioButton
        text={manuscriptText}
        graphemeText={graphemeText}
        phoneme={phoneme}
        sayAs={sayAsToggle ? sayAsText : undefined}
        language={language}
        selectedVoiceId={selectedVoiceId}
        disabled={manuscriptText.length === 0}
        onPhonemeSanitized={handlePhonemeSanitized}
      />
      {freeTextAreaValidation && (
        <Item>
          <Icon name="warning circle" color="orange" />
          Free text does not contain the chosen Grapheme
        </Item>
      )}
      <Styles.LanguageContainer>
        Language:
        <br />
        <LanguageSelector activeLang={language} onLanguageChanged={onLanguageChanged} />
      </Styles.LanguageContainer>
    </Container>
  );
}

function inputValidationMessage(message: string) {
  return (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = event.currentTarget;
    if (input.validity.patternMismatch) {
      input.setCustomValidity(message);
      return;
    }

    input.setCustomValidity('');
  };
}
